// Analytics consent.
//
// Article 11.7a Telecommunicatiewet (the Dutch ePrivacy implementation) wants
// consent *before* anything non-essential is written to the device, and GA4
// writes an `_ga` cookie the moment gtag.js runs. So gtag.js does not ship in
// the document at all: it is injected from the browser only after someone says
// yes, which makes "prior" unambiguous rather than a claim about ordering.
//
// The choice itself is stored in localStorage. Storing a refusal is allowed
// without consent precisely because it exists to honour that refusal, and
// localStorage beats a cookie here: it is never sent to the server, so it
// cannot be read as a tracking identifier.
export type Consent = "granted" | "denied";

const KEY = "sunbedly_consent";
/** Fires whenever the choice changes, so banner and loader stay in step. */
export const CONSENT_EVENT = "sunbedly:consent";

export function readConsent(): Consent | null {
  if (typeof window === "undefined") return null;
  try {
    const v = window.localStorage.getItem(KEY);
    return v === "granted" || v === "denied" ? v : null;
  } catch {
    // Private mode with storage blocked. No stored choice means no analytics,
    // which is the safe direction to fail in.
    return null;
  }
}

export function writeConsent(value: Consent): void {
  try {
    window.localStorage.setItem(KEY, value);
  } catch {
    /* nothing we can do, and nothing loads without it */
  }
  window.dispatchEvent(new CustomEvent(CONSENT_EVENT));
}

/**
 * Remove every cookie Google Analytics dropped.
 *
 * Withdrawing consent has to actually stop the processing, not merely record
 * that someone would like it to stop, so the cookies go rather than being left
 * to expire in two years.
 */
export function clearGoogleCookies(): void {
  if (typeof document === "undefined") return;
  const domain = window.location.hostname.replace(/^www\./, "");
  for (const name of document.cookie.split(";").map((c) => c.split("=")[0].trim())) {
    if (!name.startsWith("_ga")) continue;
    document.cookie = `${name}=; Max-Age=0; Path=/`;
    document.cookie = `${name}=; Max-Age=0; Path=/; Domain=.${domain}`;
  }
}

/**
 * Withdraw a previous choice, which brings the banner back.
 * Consent has to be as easy to take back as it was to give (art. 7(3) GDPR),
 * so this sits behind a permanent link in the footer, not buried in a policy.
 */
export function clearConsent(): void {
  try {
    window.localStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
  clearGoogleCookies();
  window.dispatchEvent(new CustomEvent(CONSENT_EVENT));
}
